(() => {
  const configElement = document.getElementById('OphiraConfig');
  if (!configElement) return;
  let config;
  try { config = JSON.parse(configElement.textContent); } catch { return; }
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const canMove = () => config.motion !== false && !reduced.matches;
  const origins = new WeakMap();
  const closing = new WeakSet();
  const open = (dialog, origin) => {
    if (!dialog || dialog.open) return;
    dialog.classList.remove('is-closing');
    origins.set(dialog, origin || document.activeElement);
    dialog.showModal();
    document.body.classList.add('modal-open');
    if (dialog.id === 'SearchDialog') dialog.querySelector('input')?.focus();
  };
  const close = (dialog) => {
    if (!dialog?.open || closing.has(dialog)) return;
    closing.add(dialog);
    const finish = () => {
      dialog.close();
      closing.delete(dialog);
      dialog.classList.remove('is-closing');
      if (!document.querySelector('dialog[open]')) document.body.classList.remove('modal-open');
      const origin = origins.get(dialog);
      if (origin?.isConnected) origin.focus({ preventScroll: true });
      dialog.querySelectorAll('video').forEach(video => video.pause());
    };
    if (canMove()) { dialog.classList.add('is-closing'); setTimeout(finish, 190); }
    else finish();
  };
  let toastTimer;
  const toast = (message) => {
    const element = document.querySelector('[data-toast]');
    if (!element) return;
    clearTimeout(toastTimer);
    element.textContent = message;
    element.classList.add('is-visible');
    toastTimer = setTimeout(() => element.classList.remove('is-visible'), 2800);
  };
  const money = cents => new Intl.NumberFormat(config.locale || 'en', { style: 'currency', currency: config.currency || 'CAD' }).format(cents / 100);
  const root = config.root.endsWith('/') ? config.root : `${config.root}/`;
  window.Ophira = { config, root, reduced, canMove, open, close, toast, money };
  document.addEventListener('click', event => {
    const opener = event.target.closest('[data-open]');
    if (opener && document.getElementById(opener.dataset.open)) {
      event.preventDefault();
      if (opener.dataset.open === 'SavedDialog') renderSaved();
      open(document.getElementById(opener.dataset.open), opener);
    }
    const closer = event.target.closest('[data-close]');
    if (closer) { event.preventDefault(); close(closer.closest('dialog')); }
    if (event.target instanceof HTMLDialogElement) {
      const bounds = event.target.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) close(event.target);
    }
    const tickerButton = event.target.closest('[data-pause-ticker]');
    if (tickerButton) {
      const paused = tickerButton.closest('[data-ticker]').classList.toggle('is-paused');
      tickerButton.setAttribute('aria-pressed', String(paused));
      tickerButton.firstElementChild.textContent = paused ? '▶' : 'Ⅱ';
    }
    const tab = event.target.closest('[data-mood-tab]');
    if (tab) activateMood(tab);
  });
  document.addEventListener('cancel', event => {
    if (event.target instanceof HTMLDialogElement) { event.preventDefault(); close(event.target); }
  }, true);
  document.addEventListener('close', event => {
    if (event.target instanceof HTMLDialogElement && !document.querySelector('dialog[open]')) document.body.classList.remove('modal-open');
  }, true);
  document.addEventListener('keydown', event => {
    const tab = event.target.closest('[data-mood-tab]');
    if (tab && ['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      const tabs = Array.from(tab.closest('[role=tablist]').querySelectorAll('[data-mood-tab]'));
      let next = tabs.indexOf(tab) + (['ArrowDown', 'ArrowRight'].includes(event.key) ? 1 : -1);
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = tabs.length - 1;
      const target = tabs[(next + tabs.length) % tabs.length];
      activateMood(target);
      target.focus();
    }
    if (event.key === 'Escape') document.querySelectorAll('.nav-dropdown[open],.filters[open]').forEach(details => { details.open = false; });
  });
  document.addEventListener('click', event => {
    document.querySelectorAll('.nav-dropdown[open],.filters[open]').forEach(details => {
      if (!details.contains(event.target)) details.open = false;
    });
  });
  function activateMood(tab) {
    const parent = tab.closest('[data-mood]');
    parent.querySelectorAll('[data-mood-tab]').forEach(button => {
      const active = button === tab;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', String(active));
      button.tabIndex = active ? 0 : -1;
    });
    parent.querySelectorAll('[role=tabpanel]').forEach(panel => {
      const active = panel.id === tab.dataset.moodTab;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
    });
  }
  let revealObserver;
  const initMotion = (context = document) => {
    if (!canMove() || !('IntersectionObserver' in window)) {
      document.documentElement.classList.remove('motion-ready');
      context.querySelectorAll('[data-reveal]').forEach(element => element.classList.add('is-visible'));
      return;
    }
    document.documentElement.classList.add('motion-ready');
    if (!revealObserver) revealObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); revealObserver.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px 30px 0px', threshold: 0.05 });
    context.querySelectorAll('[data-reveal]').forEach((element, index) => {
      if (element.closest('.campaign-title')) element.style.transitionDelay = `${(index % 3) * 95}ms`;
      revealObserver.observe(element);
    });
  };
  let framePending = false;
  const paintScroll = () => {
    const scrolled = window.scrollY;
    document.querySelector('[data-header]')?.classList.toggle('is-scrolled', scrolled > 20);
    const progress = document.querySelector('[data-page-progress]');
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    if (progress) progress.style.width = `${maxScroll > 0 ? scrolled / maxScroll * 100 : 0}%`;
    if (canMove() && window.innerWidth >= 900) document.querySelectorAll('[data-parallax]').forEach(element => {
      const bounds = element.getBoundingClientRect();
      if (bounds.bottom < 0 || bounds.top > window.innerHeight) return;
      const picture = element.querySelector('img');
      if (picture) picture.style.transform = `scale(1.05) translateY(${Math.max(-16, Math.min(16, bounds.top * -0.04))}px)`;
    });
    framePending = false;
  };
  window.addEventListener('scroll', () => {
    if (!framePending) { framePending = true; requestAnimationFrame(paintScroll); }
  }, { passive: true });
  reduced.addEventListener('change', () => {
    initMotion();
    if (!canMove()) document.querySelectorAll('[data-parallax] img').forEach(image => { image.style.transform = ''; });
  });
  const savedKey = 'ophira.style-board.v1';
  let saved = [];
  try {
    const stored = JSON.parse(localStorage.getItem(savedKey) || '[]');
    if (Array.isArray(stored)) saved = stored.filter(item => item && typeof item.handle === 'string' && typeof item.title === 'string' && typeof item.url === 'string').slice(0, 50);
  } catch {}
  function saveState() { try { localStorage.setItem(savedKey, JSON.stringify(saved)); } catch {} syncSaved(); }
  function syncSaved() {
    document.querySelectorAll('[data-save-product]').forEach(button => button.setAttribute('aria-pressed', String(saved.some(item => item.handle === button.dataset.handle))));
    document.querySelectorAll('[data-saved-count]').forEach(element => { element.textContent = saved.length; element.hidden = saved.length === 0; });
  }
  function safeUrl(value, image = false) {
    try {
      const url = new URL(value, location.origin);
      if (!['https:', 'http:', 'file:'].includes(url.protocol)) return '';
      if (!image && url.origin !== location.origin) return '';
      return url.href;
    } catch { return ''; }
  }
  function renderSaved() {
    const container = document.querySelector('[data-saved-list]');
    if (!container) return;
    container.replaceChildren();
    if (!saved.length) { const p = document.createElement('p'); p.textContent = config.strings.empty_saved; container.append(p); return; }
    saved.forEach(item => {
      const row = document.createElement('div'); row.className = 'saved-item';
      const image = document.createElement('img'); image.src = safeUrl(item.image || '', true); image.alt = item.title; image.loading = 'lazy';
      const content = document.createElement('div');
      const title = document.createElement('h3'); title.textContent = item.title;
      const link = document.createElement('a'); link.href = safeUrl(item.url) || root; link.textContent = config.strings.view_product; link.className = 'text-link';
      const remove = document.createElement('button'); remove.className = 'icon-button'; remove.textContent = '×'; remove.setAttribute('aria-label', config.strings.removed); remove.addEventListener('click', () => { saved = saved.filter(product => product.handle !== item.handle); saveState(); renderSaved(); });
      content.append(title, link); row.append(image, content, remove); container.append(row);
    });
  }
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-save-product]');
    if (!button) return;
    const existing = saved.some(item => item.handle === button.dataset.handle);
    if (existing) saved = saved.filter(item => item.handle !== button.dataset.handle);
    else { if (saved.length >= 50) saved.shift(); saved.push({ handle: button.dataset.handle, title: button.dataset.title, url: button.dataset.url, image: button.dataset.image || '' }); }
    saveState(); toast(existing ? config.strings.removed : config.strings.saved);
    if (canMove()) button.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.25)' }, { transform: 'scale(1)' }], { duration: 300 });
  });
  window.addEventListener('storage', event => {
    if (event.key !== savedKey) return;
    try { const value = JSON.parse(event.newValue || '[]'); if (Array.isArray(value)) saved = value.filter(item => item && typeof item.handle === 'string' && typeof item.title === 'string' && typeof item.url === 'string').slice(0,50); } catch { saved = []; }
    syncSaved(); renderSaved();
  });
  let searchTimer, searchController;
  document.addEventListener('input', event => {
    if (!event.target.matches('[data-predictive-input]')) return;
    clearTimeout(searchTimer); searchController?.abort();
    const input = event.target;
    const result = document.querySelector('[data-predictive-results]');
    if (input.value.trim().length < 2) { result?.replaceChildren(); return; }
    searchTimer = setTimeout(async () => {
      searchController = new AbortController();
      result.textContent = config.strings.loading;
      try {
        const url = new URL(`${root}search/suggest.json`, location.origin);
        url.searchParams.set('q', input.value.trim());
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '6');
        const response = await fetch(url, { signal: searchController.signal });
        if (!response.ok) throw new Error('Search failed');
        const data = await response.json();
        result.replaceChildren();
        const products = data.resources?.results?.products || [];
        products.forEach(product => {
          const anchor = document.createElement('a'); anchor.className = 'predictive-item'; anchor.href = safeUrl(product.url) || root;
          if (product.image) { const image = document.createElement('img'); image.src = safeUrl(product.image, true); image.alt = product.title; anchor.append(image); }
          const title = document.createElement('span'); title.textContent = product.title; anchor.append(title); result.append(anchor);
        });
        if (!products.length) result.textContent = document.querySelector('#SearchInput')?.placeholder || '';
      } catch (error) { if (error.name !== 'AbortError') result.textContent = config.strings.error; }
    }, 220);
  });
  document.addEventListener('shopify:section:load', event => { initMotion(event.target); syncSaved(); window.OphiraCommerce?.init(event.target); });
  document.addEventListener('shopify:block:select', event => {
    const mood = event.target.closest('[data-mood-tab]');
    if (mood) activateMood(mood);
  });
  Object.assign(window.Ophira, { initMotion, syncSaved });
  initMotion(); syncSaved(); paintScroll();
})();
